import React, { useContext } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import {
  BookOpen,
  TrendingUp,
  CreditCard,
  Calendar,
  User,
  GraduationCap,
  AlertCircle,
  CheckCircle,
  Bell,
  Info,
  MessageCircle,
} from "lucide-react";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import StatsCard from "../../components/common/StatsCard";
import WhatsAppGroups from "../../components/common/WhatsAppGroups";
import { AuthContext } from "../../contexts/AuthContext";
import {
  getMyStudentProfile,
  getStudentWhatsappGroups,
} from "../../services/students";
import { getPayments } from "../../services/fees";
import { getStudentNotices } from "../../services/notices";
import { getMyAttendance } from "../../services/attendance";
import { formatKSh } from "../../utils/currency";

const StudentDashboard: React.FC = () => {
  const authContext = useContext(AuthContext);
  const user = authContext?.user;
  const navigate = useNavigate();

  // Fetch student profile
  const {
    data: studentData,
    isLoading: studentLoading,
    error: studentError,
  } = useQuery({
    queryKey: ["student-profile"],
    queryFn: () => getMyStudentProfile(),
    enabled: !!user?.id && user?.roles?.includes("student"),
    retry: 1,
    onError: (error) => {
      console.error("Student profile fetch error:", error);
    },
  });

  // Fetch attendance summary - current month
  const { data: attendanceData } = useQuery({
    queryKey: ["student-attendance-dashboard"],
    queryFn: () => {
      const now = new Date();
      const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
      const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      return getMyAttendance({
        dateFrom: firstDay.toISOString().split("T")[0],
        dateTo: lastDay.toISOString().split("T")[0],
        limit: 100,
      });
    },
    enabled: !!user?.id,
    refetchInterval: 5 * 60 * 1000, // Refresh every 5 minutes
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

  // Get student data early to use in dependent queries
  const student = studentData?.data;

  // Fallback student data if API fails
  const fallbackStudent = {
    _id: user?.id || "unknown",
    studentId: "STU-" + (user?.id?.slice(-6) || "000000"),
    admissionNumber: "N/A",
    userInfo: {
      firstName: "Student",
      lastName: "User",
      email: user?.email || "student@example.com",
    },
    classInfo: [],
    courses: [],
    academicRecords: [],
    fees: {
      totalBalance: 0,
      feeStatus: "Unknown",
    },
    coursesInfo: [],
  };

  const currentStudent = student || fallbackStudent;

  // Fetch recent payments
  const { data: paymentsData } = useQuery({
    queryKey: ["student-payments", currentStudent?._id],
    queryFn: () => getPayments({ studentId: currentStudent?._id, limit: 5 }),
    enabled: !!currentStudent?._id && !!student, // Only fetch if we have real student data
    retry: false,
  });

  // Fetch recent notices
  const { data: noticesData } = useQuery({
    queryKey: ["student-notices-dashboard"],
    queryFn: () => getStudentNotices(),
    enabled: !!user?.id,
    retry: false,
  });

  // Fetch WhatsApp groups
  const {
    data: whatsappData,
    isLoading: whatsappLoading,
    error: whatsappError,
  } = useQuery({
    queryKey: ["student-whatsapp-groups"],
    queryFn: () => getStudentWhatsappGroups(),
    enabled: !!user?.id && user?.roles?.includes("student"),
    retry: 1,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Helper function for notice icons
  const getNoticeIcon = (type: string) => {
    switch (type) {
      case "urgent":
      case "important":
        return <AlertCircle className="h-4 w-4 text-red-600" />;
      case "academic":
        return <CheckCircle className="h-4 w-4 text-blue-600" />;
      case "info":
      case "general":
        return <Info className="h-4 w-4 text-green-600" />;
      default:
        return <Bell className="h-4 w-4 text-gray-600" />;
    }
  };

  const getNoticeTypeColor = (type: string) => {
    switch (type) {
      case "urgent":
        return "bg-gradient-to-r from-red-500 to-pink-500 text-white";
      case "important":
        return "bg-gradient-to-r from-orange-500 to-amber-500 text-white";
      case "academic":
        return "bg-gradient-to-r from-blue-500 to-indigo-500 text-white";
      case "info":
        return "bg-gradient-to-r from-green-500 to-emerald-500 text-white";
      case "general":
        return "bg-gradient-to-r from-slate-500 to-slate-600 text-white";
      default:
        return "bg-gradient-to-r from-slate-500 to-slate-600 text-white";
    }
  };

  // Check if this is an e-course student and redirect to e-course dashboard
  React.useEffect(() => {
    if (student && student.studentType === "ecourse" && !studentLoading) {
      navigate("/student/ecourse-dashboard", { replace: true });
    }
  }, [student, studentLoading, navigate]);

  // Handle loading and error states after all hooks
  if (studentLoading) {
    return <LoadingSpinner />;
  }

  // Prevent e-course students from seeing regular dashboard
  if (student && student.studentType === "ecourse") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 lg:p-6 flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="mb-4">
            <div className="mx-auto w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
              <svg
                className="w-8 h-8 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 10V3L4 14h7v7l9-11h-7z"
                />
              </svg>
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Redirecting...
          </h2>
          <p className="text-gray-600">
            Taking you to your e-learning dashboard.
          </p>
        </div>
      </div>
    );
  }

  // Check if user has student role
  if (!user?.roles?.includes("student")) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-yellow-800 font-medium">Access Restricted</h3>
          <p className="text-yellow-600 mt-1">
            This dashboard is only available for students. Your current roles:{" "}
            {user?.roles?.join(", ") || "None"}
          </p>
        </div>
      </div>
    );
  }

  if (studentError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <h3 className="text-red-800 font-medium">
            Error Loading Student Data
          </h3>
          <p className="text-red-600 mt-1">
            Unable to load your profile information. This might be because the
            student profile endpoint is not available or you don't have the
            required permissions.
          </p>
          <p className="text-red-600 mt-2 text-sm">
            Error: {(studentError as any)?.message || "Unknown error"}
          </p>
        </div>
      </div>
    );
  }

  // Calculate stats with proper null checks
  const attendancePercentage = attendanceData?.summary?.attendancePercentage;
  const attendanceRate =
    typeof attendancePercentage === "string"
      ? parseFloat(attendancePercentage) || 0
      : attendancePercentage || 0;
  const totalDays = attendanceData?.summary?.total || 0;
  const presentDays = attendanceData?.summary?.present || 0;
  const absentDays = attendanceData?.summary?.absent || 0;

  const totalCourses = currentStudent?.courses?.length || 0;
  const completedRecords = currentStudent?.academicRecords?.length || 0;

  // Calculate fee balance from student data
  const feeBalance = currentStudent?.fees?.totalBalance || 0;
  const feeStatus = currentStudent?.fees?.feeStatus || "Unknown";

  // Get recent payments for notifications
  const recentPayments = paymentsData?.data || [];

  const currentDate = new Date().toLocaleDateString("en-US", {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-0 sm:p-4 lg:p-6">
      {/* Header */}
      <div className="text-right mb-2">
        <p className="text-sm font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
          {currentDate}
        </p>
      </div>
      <div className="mb-6 lg:mb-8">
        <h1 className="text-xl sm:text-2xl md:text-4xl font-bold text-slate-900">
          Welcome back, {currentStudent?.userInfo?.firstName || "Student"}!
        </h1>
        <p className="text-slate-600 mt-2 text-sm sm:text-base font-medium">
          Admission No:{" "}
          {currentStudent?.admissionNumber ||
            currentStudent?.studentId ||
            "N/A"}
          {currentStudent?.classInfo && currentStudent.classInfo.length > 0
            ? ` • Class: ${
                currentStudent.classInfo[0].name ||
                currentStudent.classInfo[0].className
              }`
            : (currentStudent as any)?.currentClassId?.name
            ? ` • Class: ${(currentStudent as any).currentClassId.name}`
            : ""}
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 lg:mb-8">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
              <CreditCard className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-xl lg:text-2xl font-bold text-slate-900">
              {formatKSh(feeBalance)}
            </p>
            <p className="text-xs lg:text-sm text-slate-600 font-medium mt-1">
              Fee Balance
            </p>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
              <BookOpen className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-xl lg:text-2xl font-bold text-slate-900">
              {totalCourses}
            </p>
            <p className="text-xs lg:text-sm text-slate-600 font-medium mt-1">
              Total Courses
            </p>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center shadow-md">
              <Calendar className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-xl lg:text-2xl font-bold text-slate-900">
              {attendanceRate.toFixed(1)}%
            </p>
            <p className="text-xs lg:text-sm text-slate-600 font-medium mt-1">
              Attendance Rate
            </p>
          </div>
        </div>

        <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 p-4 lg:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-3">
            <div className="w-12 h-12 lg:w-14 lg:h-14 rounded-2xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md">
              <MessageCircle className="w-6 h-6 lg:w-7 lg:h-7 text-white" />
            </div>
          </div>
          <div>
            <p className="text-xl lg:text-2xl font-bold text-slate-900">
              {whatsappData?.data?.length || 0}
            </p>
            <p className="text-xs lg:text-sm text-slate-600 font-medium mt-1">
              WhatsApp Groups
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Current Courses */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-4 lg:p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg lg:text-xl font-bold text-slate-900">
              Current Courses
            </h2>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md">
              <BookOpen className="h-5 w-5 text-white" />
            </div>
          </div>
          {currentStudent?.coursesInfo &&
          currentStudent.coursesInfo.length > 0 ? (
            <div className="space-y-3">
              {currentStudent.coursesInfo
                .slice(0, 5)
                .map((course: any, index: number) => (
                  <div
                    key={course._id || `course-${index}`}
                    className="flex items-center justify-between p-3 bg-gradient-to-br from-slate-50 to-blue-50 rounded-xl hover:shadow-md transition-all duration-300 border border-slate-200/50"
                  >
                    <div className="flex items-center space-x-3 min-w-0 flex-1">
                      <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm">
                        <BookOpen className="h-5 w-5 text-white" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {course.name || "Course"}({course.level})
                        </h3>
                        <p className="text-sm text-slate-600 truncate">
                          {course.code || "No code"}
                          {course.instructor && (
                            <span className="ml-2 text-slate-500 hidden sm:inline">
                              • {course.instructor}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex-shrink-0">
                      <span className="inline-flex px-3 py-1 text-xs font-semibold rounded-full bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-sm">
                        Enrolled
                      </span>
                    </div>
                  </div>
                ))}
              {currentStudent.coursesInfo.length > 5 && (
                <div className="text-center pt-2">
                  <button className="text-blue-600 hover:text-blue-700 text-sm font-semibold hover:underline">
                    View all {currentStudent.coursesInfo.length} courses
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="w-16 h-16 bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <BookOpen className="h-8 w-8 text-slate-500" />
              </div>
              <h3 className="mt-2 text-sm font-semibold text-slate-900">
                No courses found
              </h3>
              <p className="mt-1 text-sm text-slate-500">
                You haven't enrolled in any courses yet.
              </p>
            </div>
          )}
        </div>

        {/* Recent Grades & Notifications */}
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg lg:text-xl font-bold text-slate-900">
              Recent Updates
            </h2>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-md">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
          </div>

          {/* Real data - Recent payments and fee updates */}
          <div className="space-y-3">
            {recentPayments && recentPayments.length > 0
              ? recentPayments
                  .slice(0, 3)
                  .map((payment: any, index: number) => (
                    <div
                      key={payment._id || `payment-${index}`}
                      className="flex items-start p-3 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/50 rounded-xl hover:shadow-md transition-all duration-300"
                    >
                      <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                        <CheckCircle className="h-4 w-4 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center justify-between mb-1">
                          <h3 className="font-semibold text-green-900">
                            Payment Received
                          </h3>
                          <span className="text-xs text-white bg-gradient-to-r from-green-500 to-emerald-500 px-2 py-1 rounded-full font-semibold shadow-sm">
                            Payment
                          </span>
                        </div>
                        <p className="text-sm text-green-800 mb-2 font-medium">
                          {formatKSh(payment.amount || 0)} payment received via{" "}
                          {payment.paymentMethod || "N/A"}
                          {payment.referenceNumber &&
                            ` (Ref: ${payment.referenceNumber})`}
                        </p>
                        <span className="text-xs text-green-600 font-medium">
                          {payment.paymentDate
                            ? new Date(payment.paymentDate).toLocaleDateString()
                            : "Recent"}
                        </span>
                      </div>
                    </div>
                  ))
              : null}

            {/* Fee Status Alert */}
            {feeBalance > 0 && (
              <div className="flex items-start p-3 bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200/50 rounded-xl hover:shadow-md transition-all duration-300">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                  <AlertCircle className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-yellow-900">
                      Outstanding Fee Balance
                    </h3>
                    <span className="text-xs text-white bg-gradient-to-r from-yellow-500 to-orange-500 px-2 py-1 rounded-full font-semibold shadow-sm">
                      Fee
                    </span>
                  </div>
                  <p className="text-sm text-yellow-800 mb-2 font-medium">
                    You have an outstanding balance of {formatKSh(feeBalance)}.
                    Status: {feeStatus}
                  </p>
                  <span className="text-xs text-yellow-600 font-medium">
                    Current
                  </span>
                </div>
              </div>
            )}

            {/* Attendance Status */}
            {attendanceRate < 75 && (
              <div className="flex items-start p-3 bg-gradient-to-br from-red-50 to-pink-50 border border-red-200/50 rounded-xl hover:shadow-md transition-all duration-300">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-red-500 to-pink-500 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                  <AlertCircle className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-red-900">
                      Low Attendance Alert
                    </h3>
                    <span className="text-xs text-white bg-gradient-to-r from-red-500 to-pink-500 px-2 py-1 rounded-full font-semibold shadow-sm">
                      Alert
                    </span>
                  </div>
                  <p className="text-sm text-red-800 mb-2 font-medium">
                    Your attendance rate is {attendanceRate.toFixed(1)}%. Please
                    improve attendance.
                  </p>
                  <span className="text-xs text-red-600 font-medium">
                    Current
                  </span>
                </div>
              </div>
            )}

            {/* Good attendance status */}
            {attendanceRate >= 75 && (
              <div className="flex items-start p-3 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/50 rounded-xl hover:shadow-md transition-all duration-300">
                <div className="w-8 h-8 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0 shadow-sm">
                  <CheckCircle className="h-4 w-4 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="font-semibold text-blue-900">
                      Good Attendance
                    </h3>
                    <span className="text-xs text-white bg-gradient-to-r from-blue-500 to-indigo-500 px-2 py-1 rounded-full font-semibold shadow-sm">
                      Info
                    </span>
                  </div>
                  <p className="text-sm text-blue-800 mb-2 font-medium">
                    Your attendance rate is {attendanceRate.toFixed(1)}%. Keep
                    up the good work!
                  </p>
                  <span className="text-xs text-blue-600 font-medium">
                    Current
                  </span>
                </div>
              </div>
            )}

            {/* Show message if no recent updates */}
            {(!recentPayments || recentPayments.length === 0) &&
              feeBalance === 0 && (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                    <TrendingUp className="h-8 w-8 text-slate-500" />
                  </div>
                  <h3 className="mt-2 text-sm font-semibold text-slate-900">
                    No recent updates
                  </h3>
                  <p className="mt-1 text-sm text-slate-500">
                    You're all caught up! Check back later for new updates.
                  </p>
                </div>
              )}

            {/* View All Button */}
            <div className="text-center pt-2">
              <button className="text-blue-600 hover:text-blue-700 text-sm font-semibold hover:underline">
                View all notifications
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* WhatsApp Groups Section */}
      <div className="mt-8">
        <WhatsAppGroups
          groups={whatsappData?.data || []}
          isLoading={whatsappLoading}
          error={whatsappError ? "Failed to load WhatsApp groups" : null}
        />
      </div>

      {/* Recent Notices/Announcements */}
      <div className="mt-8">
        <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-sm border border-slate-200/60 p-6 hover:shadow-lg transition-all duration-300">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg lg:text-xl font-bold text-slate-900">
              Recent Announcements
            </h2>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center shadow-md">
                <Bell className="h-5 w-5 text-white" />
              </div>
              <button
                onClick={() => navigate("/student/notices")}
                className="text-blue-600 hover:text-blue-700 text-sm font-semibold hover:underline"
              >
                All
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {noticesData?.data && noticesData.data.length > 0 ? (
              noticesData.data.slice(0, 3).map((notice: any) => (
                <div
                  key={notice._id}
                  className="flex items-start p-4 border border-slate-200 rounded-xl hover:bg-slate-50 hover:shadow-md transition-all duration-300"
                >
                  <div className="mr-3 mt-0.5 flex-shrink-0">
                    {getNoticeIcon(notice.type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-slate-900 truncate">
                        {notice.title}
                      </h3>
                      <div className="flex items-center gap-2 ml-2">
                        <span
                          className={`text-xs px-2 py-1 rounded-full font-semibold ${getNoticeTypeColor(
                            notice.type
                          )}`}
                        >
                          {notice.type}
                        </span>
                        {notice.priority === "high" && (
                          <span className="text-xs px-2 py-1 rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-white font-semibold shadow-sm">
                            Urgent!
                          </span>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 line-clamp-2 mb-2">
                      {notice.content}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-slate-500 font-medium">
                        By: {notice.author?.name || "Admin"}
                      </span>
                      <span className="text-xs text-slate-500 font-medium">
                        {new Date(notice.publishDate).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-gradient-to-r from-slate-200 to-slate-300 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                  <Bell className="h-8 w-8 text-slate-500" />
                </div>
                <h3 className="mt-2 text-sm font-semibold text-slate-900">
                  No announcements
                </h3>
                <p className="mt-1 text-sm text-slate-500">
                  There are no recent announcements to display.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <h2 className="text-lg lg:text-xl font-bold text-slate-900 mb-4">
          Quick Actions
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <button
            onClick={() => navigate("/student/grades")}
            className="flex items-center justify-center p-5 bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200/50 rounded-2xl hover:shadow-xl transition-all duration-300 group hover:scale-105"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center shadow-md mr-3 group-hover:scale-110 transition-transform">
              <GraduationCap className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900">View Grades</span>
          </button>
          <button
            onClick={() => navigate("/student/attendance")}
            className="flex items-center justify-center p-5 bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200/50 rounded-2xl hover:shadow-xl transition-all duration-300 group hover:scale-105"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center shadow-md mr-3 group-hover:scale-110 transition-transform">
              <Calendar className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Attendance</span>
          </button>
          <button
            onClick={() => navigate("/student/fees")}
            className="flex items-center justify-center p-5 bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200/50 rounded-2xl hover:shadow-xl transition-all duration-300 group hover:scale-105"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center shadow-md mr-3 group-hover:scale-110 transition-transform">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Pay Fees</span>
          </button>
          <button
            onClick={() => navigate("/student/notices")}
            className="flex items-center justify-center p-5 bg-gradient-to-br from-yellow-50 to-orange-50 border border-yellow-200/50 rounded-2xl hover:shadow-xl transition-all duration-300 group hover:scale-105"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-yellow-500 to-orange-500 flex items-center justify-center shadow-md mr-3 group-hover:scale-110 transition-transform">
              <Bell className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Notices</span>
          </button>
          <button
            onClick={() => navigate("/student/profile")}
            className="flex items-center justify-center p-5 bg-gradient-to-br from-orange-50 to-red-50 border border-orange-200/50 rounded-2xl hover:shadow-xl transition-all duration-300 group hover:scale-105"
          >
            <div className="w-10 h-10 rounded-xl bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center shadow-md mr-3 group-hover:scale-110 transition-transform">
              <User className="h-5 w-5 text-white" />
            </div>
            <span className="font-semibold text-slate-900">Profile</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentDashboard;
